import { useCallback } from 'react';
import { LATEST_VERSION, isNewerVersion, releaseNotes } from '@/data/releaseNotes';
import type { ReleaseNote } from '@/data/releaseNotes';
import { useAuthStore } from '@/store/useAuthStore';
import { useReleaseNotesStore } from '@/store/useReleaseNotesStore';

interface VisibleReleaseNotes {
  /** Alle Einträge für Verwaltung/SuperAdmin, leer für Mitarbeiter — neueste zuerst. */
  notes: ReleaseNote[];
  /** true, wenn es einen sichtbaren Eintrag gibt, der neuer ist als die zuletzt gesehene Version. */
  hasUnseen: boolean;
  /** null = dieser Browser hat noch nie etwas gesehen. */
  lastSeenVersion: string | null;
  markAllSeen: () => void;
}

/**
 * Neuerungen sieht nur die Verwaltung (inkl. SuperAdmin). Mitarbeiter bekommen eine leere
 * Liste und damit weder Dialog noch Hinweispunkt.
 *
 * Gesehen wird immer bis zur aktuellen App-Version markiert; ob etwas neu ist, entscheidet
 * der Semver-Vergleich mit dem neuesten Eintrag.
 */
export function useVisibleReleaseNotes(): VisibleReleaseNotes {
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const lastSeenVersion = useReleaseNotesStore((s) => s.lastSeenVersion);
  const markSeen = useReleaseNotesStore((s) => s.markSeen);

  const notes = isAdmin ? releaseNotes : [];
  const latestVisible = notes[0]?.version;
  const hasUnseen =
    lastSeenVersion !== null &&
    latestVisible !== undefined &&
    isNewerVersion(latestVisible, lastSeenVersion);

  const markAllSeen = useCallback(() => markSeen(LATEST_VERSION), [markSeen]);

  return { notes, hasUnseen, lastSeenVersion, markAllSeen };
}
